import { useEffect, useState, useRef, useMemo } from 'react';
import { useDataProvider } from 'react-admin';

export const commentImageBlobCache = new Map();

const resolveIsServiceInspection = (resource, serviceInspectionId, serviceRequestId) => {
    if (resource) return resource === 'service-inspection';
    if (serviceInspectionId && !serviceRequestId) return true;
    if (serviceRequestId && !serviceInspectionId) return false;
    return globalThis.location.pathname.includes('/service-inspection');
};

const loadImageBlob = async (imageName, serviceRequestId, serviceInspectionId, changeId, dataProvider, isServiceInspection) => {
    const relativeUrl = `/${isServiceInspection ? 'service-inspection' : 'service-request'}/${isServiceInspection ? serviceInspectionId : serviceRequestId}/change/${changeId}/${encodeURIComponent(imageName)}`;
    const blob = await dataProvider.getBlob(relativeUrl);
    return URL.createObjectURL(blob);
};

export const getCacheKey = (serviceRequestId, serviceInspectionId, changeId, imageName, useThumbnails, isServiceInspection) => {
    return `${isServiceInspection ? serviceInspectionId : serviceRequestId}-${changeId}-${imageName}-${useThumbnails ? 'thumb' : 'full'}`;
};

export const clearCacheForChange = (serviceRequestId, serviceInspectionId, changeId, isServiceInspection) => {
    const keysToDelete = [];
    commentImageBlobCache.forEach((blobUrl, key) => {
        if (key.startsWith(`${isServiceInspection ? serviceInspectionId : serviceRequestId}-${changeId}-`)) {
            keysToDelete.push(key);
            URL.revokeObjectURL(blobUrl);
        }
    });
    keysToDelete.forEach(key => commentImageBlobCache.delete(key));
};

export const clearCacheForRemovedImages = (serviceRequestId, serviceInspectionId, changeId, currentImages, isServiceInspection) => {
    const currentImageSet = new Set(currentImages);
    const keysToDelete = [];
    const prefix = `${isServiceInspection ? serviceInspectionId : serviceRequestId}-${changeId}-`;

    commentImageBlobCache.forEach((blobUrl, key) => {
        if (key.startsWith(prefix)) {
            const keySuffix = key.substring(prefix.length);
            const isThumbnail = keySuffix.endsWith('-thumb');
            const isFull = keySuffix.endsWith('-full');

            if (isThumbnail || isFull) {
                const imageNameSuffix = isThumbnail ? '-thumb' : '-full';
                const imageName = keySuffix.substring(0, keySuffix.length - imageNameSuffix.length);

                if (!currentImageSet.has(imageName)) {
                    keysToDelete.push(key);
                    URL.revokeObjectURL(blobUrl);
                }
            }
        }
    });

    keysToDelete.forEach(key => commentImageBlobCache.delete(key));
};

const updateImageBlobUrl = (setImageBlobUrls, isCancelled, index, blobUrl) => {
    if (!isCancelled) {
        setImageBlobUrls(prev => ({ ...prev, [index]: blobUrl }));
    }
};

const loadSingleImage = async ({
    imageName,
    index,
    serviceRequestId,
    serviceInspectionId,
    changeId,
    dataProvider,
    isCancelled,
    setImageBlobUrls,
    useThumbnails,
    isServiceInspection,
}) => {
    const cacheKey = getCacheKey(serviceRequestId, serviceInspectionId, changeId, imageName, useThumbnails, isServiceInspection);

    if (commentImageBlobCache.has(cacheKey)) {
        const cachedUrl = commentImageBlobCache.get(cacheKey);
        updateImageBlobUrl(setImageBlobUrls, isCancelled, index, cachedUrl);
        return;
    }

    const imageToLoad = useThumbnails ? `thumb_${imageName}` : imageName;

    try {
        const blobUrl = await loadImageBlob(imageToLoad, serviceRequestId, serviceInspectionId, changeId, dataProvider, isServiceInspection);
        commentImageBlobCache.set(cacheKey, blobUrl);
        updateImageBlobUrl(setImageBlobUrls, isCancelled, index, blobUrl);
    } catch (err) {
        console.error(`Error loading image ${imageName}:`, err);
    }
};

const useCommentImages = ({ serviceRequestId, serviceInspectionId, changeId, images, useThumbnails = true, resource }) => {
    const dataProvider = useDataProvider();
    const [imageBlobUrls, setImageBlobUrls] = useState({});
    const loadedBlobUrlsRef = useRef(new Set());
    const previousImagesRef = useRef(null);
    const isServiceInspection = resolveIsServiceInspection(resource, serviceInspectionId, serviceRequestId);

    const normalizeImageName = image => {
        if (typeof image === 'string') return image;
        if (image?.imageName) return image.imageName;
        if (image?.name) return image.name;
        return image;
    };

    const imagesKey = useMemo(() => {
        if (!images?.length) return null;
        return images.map(normalizeImageName).join(',');
    }, [images]);

    useEffect(() => {
        const currentId = isServiceInspection ? serviceInspectionId : serviceRequestId;
        if (!currentId || !changeId || !images?.length) {
            setImageBlobUrls({});
            loadedBlobUrlsRef.current.clear();
            previousImagesRef.current = null;
            return;
        }

        if (previousImagesRef.current !== null && previousImagesRef.current !== imagesKey) {
            const imageNames = images.map(normalizeImageName);
            clearCacheForRemovedImages(serviceRequestId, serviceInspectionId, changeId, imageNames, isServiceInspection);
        }
        previousImagesRef.current = imagesKey;

        let isCancelled = false;
        const currentLoadedBlobUrls = loadedBlobUrlsRef.current;
        currentLoadedBlobUrls.clear();

        const computeNextState = (prev, updater) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            trackBlobUrls(next, currentLoadedBlobUrls);
            return next;
        };

        const imageUpdater = updater => {
            if (isCancelled) return;
            setImageBlobUrls(prev => computeNextState(prev, updater));
        };

        const trackBlobUrls = (urls, loadedBlobUrls) => {
            Object.values(urls).forEach(url => {
                if (url && typeof url === 'string' && url.startsWith('blob:')) {
                    loadedBlobUrls.add(url);
                }
            });
        };

        const getInitialCachedUrls = () => {
            const initialUrls = {};
            images.forEach((image, index) => {
                const imageName = normalizeImageName(image);
                const cacheKey = getCacheKey(serviceRequestId, serviceInspectionId, changeId, imageName, useThumbnails, isServiceInspection);
                const cachedUrl = commentImageBlobCache.get(cacheKey);
                if (cachedUrl) {
                    initialUrls[index] = cachedUrl;
                    currentLoadedBlobUrls.add(cachedUrl);
                }
            });
            return initialUrls;
        };

        const loadAllImages = async () => {
            const initialUrls = getInitialCachedUrls();

            if (!isCancelled && Object.keys(initialUrls).length > 0) {
                setImageBlobUrls(initialUrls);
            }

            const loadPromises = images.map((image, index) => {
                const imageName = normalizeImageName(image);
                return loadSingleImage({
                    imageName,
                    index,
                    serviceRequestId,
                    serviceInspectionId,
                    changeId,
                    dataProvider,
                    isCancelled,
                    setImageBlobUrls: imageUpdater,
                    useThumbnails,
                    isServiceInspection,
                });
            });

            await Promise.all(loadPromises);
        };

        const isBlobUrl = blobUrl => {
            return blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:');
        };

        const cleanupBlobUrls = () => {
            const blobUrlsToCleanup = Array.from(currentLoadedBlobUrls);
            const cacheValueSet = new Set(commentImageBlobCache.values());
            blobUrlsToCleanup.forEach(blobUrl => {
                if (!isBlobUrl(blobUrl)) return;
                const isInCache = cacheValueSet.has(blobUrl);
                if (!isInCache) {
                    URL.revokeObjectURL(blobUrl);
                }
            });
            currentLoadedBlobUrls.clear();
        };

        loadAllImages();

        return () => {
            isCancelled = true;
            cleanupBlobUrls();
        };
    }, [serviceRequestId, serviceInspectionId, changeId, images, imagesKey, dataProvider, useThumbnails, isServiceInspection]);

    return { imageBlobUrls };
};

export default useCommentImages;
